import { FC, useContext } from "react";

import { AccessTime, Assignment, Message, Settings } from "@mui/icons-material";
import {
  IconButton,
  Theme,
  Tooltip,
  useMediaQuery,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";

import { Permission } from "@/components/UserManagement/types";
import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";

import { collapsedAppIconSx } from "./collapsedAppIconSx";
import { frameNavItemSx } from "./frameNavItemSx";

interface Props {
  dense?: boolean;
  logout?: () => void;
  onMenuClick?: () => void;
  collapsed?: boolean;
}

const RubiMessageMenu: FC<Props> = ({ dense = false, collapsed = false }) => {
  const theme = useTheme();
  const { t } = useTranslation("message");
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));
  const { setCollapsed } = useSidebar();
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);
  const orgID = getOrgIDFromURL() ?? currentUser?.rubi_org_id ?? "";

  const currentUserPermissions =
    currentUser?.permissions?.map((p) => p.title as Permission) ?? [];
  const userPermissions = userPermissionChecks(currentUserPermissions);

  const { pathname } = useLocation();

  const hideSideBar = () => {
    setCollapsed(true);
  };

  const onConversation = pathname.startsWith(
    `/rubimessage/${orgID}/conversation`,
  );

  const menuItems = [
    {
      to: `/rubimessage/${orgID}`,
      text: t("menu.message"),
      icon: <Message />,
      end: true,
      extraClass: onConversation ? "active" : undefined,
    },
    ...(userPermissions.canViewMessageAllHistory
      ? [
          {
            to: `/rubimessage/${orgID}/history`,
            text: t("menu.allHistory"),
            icon: <AccessTime />,
            end: false,
            extraClass: undefined,
          },
        ]
      : []),
    ...(userPermissions.canWriteMessageTemplate
      ? [
          {
            to: `/rubimessage/${orgID}/settings`,
            text: t("menu.settings"),
            icon: <Settings />,
            end: false,
            extraClass: undefined,
          },
        ]
      : []),
  ];

  return (
    <div className="menu">
      {!collapsed ? (
        <div className="sidebar-top">
          <div className="app-name">
            Rubi<span>MESSAGE</span>
          </div>
        </div>
      ) : (
        <div className="application-item">
          <Tooltip title="RubiMESSAGE" placement="right" arrow>
            <IconButton
              aria-label={t("menu.appAriaLabel")}
              size="large"
              disableRipple
              sx={collapsedAppIconSx}
            >
              <Assignment />
            </IconButton>
          </Tooltip>
        </div>
      )}
      <div className="left-menu">
        {menuItems.map((item) => (
          <Tooltip
            key={item.to}
            title={collapsed ? item.text : ""}
            placement="right"
            arrow
          >
            <MenuItem
              component={NavLink}
              to={item.to}
              end={item.end}
              className={item.extraClass}
              onClick={isSmall ? hideSideBar : undefined}
              sx={[
                frameNavItemSx(),
                {
                  pl: collapsed ? 1 : 2,
                  py: dense ? 1 : 1.5,
                  minHeight: collapsed ? 48 : "auto",
                  justifyContent: collapsed ? "center" : "flex-start",
                },
              ]}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && <ListItemText primary={item.text} />}
            </MenuItem>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default RubiMessageMenu;
